/**
 * Gestor de captura de elementos para el PDF
 */
import html2canvas from 'html2canvas';

class CaptureManager {
    constructor(options = {}) {
        this.options = {
            captureQuality: options.captureQuality || 0.95,
            ...options
        };
    }

    /**
     * Verificar si un elemento es visible
     */
    isElementVisible(element) {
    try {
        if (!element) return false;
        
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        
        // ✅ VERIFICACIONES BÁSICAS
        const isVisible = rect.width > 0 && 
                         rect.height > 0 && 
                         style.display !== 'none' && 
                         style.visibility !== 'hidden' &&
                         style.opacity !== '0';
        
        // ✅ VERIFICACIÓN ESPECIAL PARA CONTENIDO DINÁMICO (SmartComponentRenderer)
        const hasLoadingState = element.querySelector('[data-loading="true"]');
        if (hasLoadingState) {
            console.log('⏳ Elemento aún cargando, esperando...');
            return false;
        }
        
        // ✅ VERIFICAR QUE TENGA CONTENIDO VISUAL
        const hasVisualContent = !!(
            element.querySelector('canvas') ||
            element.querySelector('.chart') ||
            element.querySelector('.map') ||
            element.querySelector('svg') ||
            element.querySelector('img') ||
            (element.textContent && element.textContent.trim().length > 0)
        );
        
        return isVisible && hasVisualContent;
        
    } catch (error) {
        console.warn('Error verificando visibilidad:', error);
        return false;
    }
}

    // isElementVisible(element) {
    //     if (!element) return false;
        
    //     const rect = element.getBoundingClientRect();
    //     const style = window.getComputedStyle(element);
        
    //     return rect.width > 0 && 
    //            rect.height > 0 && 
    //            style.display !== 'none' && 
    //            style.visibility !== 'hidden' &&
    //            parseFloat(style.opacity) > 0;
    // }

    /**
     * Detectar si el elemento tiene contenido visual
     */
    hasVisualContent(element) {
        return element.querySelector(`
            canvas,
            .chart-container,
            .echarts-container,
            [class*="Chart"],
            [class*="chart"],
            [id*="chart"],
            [data-chart],
            [id*="map"],
            .map-container,
            [class*="map"],
            [data-map],
            .leaflet-container,
            .google-map,
            .highcharts-container,
            .plotly-graph-div,
            .d3-chart,
            svg[class*="chart"],
            .progress-professional,
            .recent-activity-card
        `);
    }

    /**
     * Detectar si es solo tabla
     */
    hasTableOnly(element) {
        const hasTable = element.querySelector(`
            table,
            .table,
            [role="table"],
            .tanstack-table,
            .data-grid,
            .list-striped,
            [class*="table"]
        `);
        
        const hasVisual = this.hasVisualContent(element);
        
        return hasTable && !hasVisual;
    }

    /**
     * Detectar si es fila solo de tabla (para recorridos)
     */
    isTableOnlyRow(element) {
        const hasTable = element.querySelector(`
            table,
            .table,
            [role="table"],
            .tanstack-table,
            .data-grid,
            .list-striped,
            [class*="table"]
        `);
        
        const hasVisual = this.hasVisualContent(element);
        
        // Es tabla solo si tiene tabla pero NO tiene contenido visual
        return hasTable && !hasVisual;
    }


    /**
 * Preparar elemento para captura - MEJORADO para manejar scroll
 */
async prepareElementForCapture(element) {
    const originalStyles = new Map();
    
    // 🔍 DETECTAR ELEMENTOS CON SCROLL DENTRO DEL COMPONENTE
    const scrollableElements = this.findScrollableElements(element);
    
    console.log(`🔄 Preparando captura - ${scrollableElements.length} elementos con scroll detectados`);
    
    // ✅ EXPANDIR ELEMENTOS CON SCROLL
    for (const scrollElement of scrollableElements) {
        const originalStyle = scrollElement.getAttribute('style') || '';
        originalStyles.set(scrollElement, originalStyle);
        
        // Expandir para mostrar todo el contenido
        const fullHeight = scrollElement.scrollHeight;
        const fullWidth = scrollElement.scrollWidth;
        
        console.log(`📏 Expandiendo elemento: ${scrollElement.offsetHeight}px → ${fullHeight}px`);
        
        scrollElement.style.height = `${fullHeight}px`;
        scrollElement.style.maxHeight = 'none';
        scrollElement.style.overflow = 'visible';
        
        if (fullWidth > scrollElement.offsetWidth) {
            scrollElement.style.width = `${fullWidth}px`;
            scrollElement.style.maxWidth = 'none';
        }
    }
    
    // 🔍 DETECTAR TABLAS COLAPSADAS O DESPLEGABLES
    const collapsibleTables = element.querySelectorAll('[data-bs-toggle="collapse"], .collapse, .accordion-collapse');
    
    for (const collapsible of collapsibleTables) {
        const originalStyle = collapsible.getAttribute('style') || '';
        originalStyles.set(collapsible, originalStyle);
        
        // Forzar mostrar contenido colapsado
        collapsible.classList.add('show');
        collapsible.style.height = 'auto';
        collapsible.style.display = 'block';
        
        console.log(`📂 Elemento desplegable expandido`);
    }
    
    // Guardar estilos originales para restaurar después
    element._originalStyles = originalStyles;
    
    // Esperar a que se renderice el contenido expandido
    return new Promise(resolve => setTimeout(resolve, 500));
}

/**
 * Encontrar elementos con scroll dentro de un componente
 */
findScrollableElements(container) {
    const scrollableElements = [];
    
    // Selectores comunes para elementos con scroll
    const scrollSelectors = [
        '.overflow-auto',
        '.overflow-scroll', 
        '.overflow-y-auto',
        '.overflow-y-scroll',
        '[style*="overflow: auto"]',
        '[style*="overflow: scroll"]',
        '[style*="overflow-y: auto"]',
        '[style*="overflow-y: scroll"]',
        '.table-responsive',
        '.scrollable-list',
        '.alert-list', // Específico para listas de alertas
        '.vehicle-list' // Específico para listas de vehículos
    ];
    
    scrollSelectors.forEach(selector => {
        const elements = container.querySelectorAll(selector);
        elements.forEach(el => {
            // Verificar si realmente tiene scroll
            if (el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth) {
                scrollableElements.push(el);
                console.log(`🔄 Scroll detectado en: ${selector}`);
            }
        });
    });
    
    return scrollableElements;
}


    /**
     * Preparar elemento para captura (expandir contenedores, etc.)
     */
    async prepareElementForCapture_respaldo(element) {
        // Guardar estados originales
        const originalStyles = new Map();
        
        // Expandir contenedores con scroll
        const scrollContainers = element.querySelectorAll('[style*="height"], .overflow-auto, .overflow-hidden');
        
        scrollContainers.forEach(container => {
            const originalStyle = container.getAttribute('style') || '';
            originalStyles.set(container, originalStyle);
            
            // Expandir altura para mostrar todo el contenido
            const scrollHeight = container.scrollHeight;
            if (scrollHeight > container.clientHeight) {
                container.style.height = `${scrollHeight}px`;
                container.style.overflow = 'visible';
            }
        });
        
        // Asegurar que los gráficos estén completamente renderizados
        const chartElements = element.querySelectorAll('canvas, .echarts-container');
        chartElements.forEach(chart => {
            if (chart.style.height && chart.style.height.includes('px')) {
                const originalHeight = chart.getAttribute('style') || '';
                originalStyles.set(chart, originalHeight);
                // Mantener altura original de gráficos
            }
        });
        
        // Guardar referencia para restauración
        element._originalStyles = originalStyles;
        
        // Esperar a que se apliquen los cambios
        return new Promise(resolve => setTimeout(resolve, 100));
    }

    /**
     * Restaurar elemento después de la captura
     */
    async restoreElementAfterCapture(element) {
        if (element._originalStyles) {
            element._originalStyles.forEach((originalStyle, el) => {
                if (originalStyle) {
                    el.setAttribute('style', originalStyle);
                } else {
                    el.removeAttribute('style');
                }
            });
            delete element._originalStyles;
        }
    }

    /**
     * Preparar tabla para captura
     */
    async prepareTableForCapture(tableContainer) {
        const originalStyles = new Map();
        
        // Expandir contenedor de tabla
        if (tableContainer.style.height) {
            originalStyles.set(tableContainer, tableContainer.getAttribute('style') || '');
            
            // Calcular altura necesaria
            const table = tableContainer.querySelector('table, [role="table"]');
            if (table) {
                const tableHeight = table.scrollHeight || table.offsetHeight;
                const padding = 100; // Espacio extra para controles
                
                tableContainer.style.height = `${tableHeight + padding}px`;
                tableContainer.style.overflow = 'visible';
            }
        }
        
        // Expandir tabla interna si tiene scroll
        const scrollableElements = tableContainer.querySelectorAll('.overflow-auto, .table-responsive, [style*="overflow"]');
        scrollableElements.forEach(element => {
            const originalStyle = element.getAttribute('style') || '';
            originalStyles.set(element, originalStyle);
            
            element.style.overflow = 'visible';
            element.style.height = 'auto';
            element.style.maxHeight = 'none';
        });
        
        tableContainer._originalStyles = originalStyles;
        
        // Esperar renderizado
        return new Promise(resolve => setTimeout(resolve, 200));
    }

getOptionsForElementType(elementType, element) {
    const baseOptions = {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 15000,
        removeContainer: true,
        foreignObjectRendering: false,
        scale: 2,
        onclone: (clonedDoc) => {
            // ✅ TU CÓDIGO ORIGINAL DE BADGES QUE FUNCIONA
            console.log('🔧 === REEMPLAZO COMPLETO DE BADGES ===');
            const badges = clonedDoc.querySelectorAll('.badge.bg-200');
            badges.forEach((badge, index) => {
                const text = badge.textContent.trim();
                const newBadge = clonedDoc.createElement('span');
                newBadge.textContent = text;
                newBadge.className = 'badge rounded-pill bg-custom-fix';
                newBadge.style.cssText = `
                    background-color: #e9ecef !important;
                    color: #0d6efd !important;
                    border: 1px solid #dee2e6 !important;
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    padding: 4px 8px !important;
                    border-radius: 50rem !important;
                    display: inline-block !important;
                    min-height: 20px !important;
                    line-height: 1.2 !important;
                `;
                badge.parentNode.replaceChild(newBadge, badge);
            });

            // ✅ TU CÓDIGO ORIGINAL DE DONAS
            const chartContainers = clonedDoc.querySelectorAll('.position-relative.py-2');
            chartContainers.forEach(chartContainer => {
                const canvasEl = chartContainer.querySelector('canvas');
                if (canvasEl) {
                    const canvRect = canvasEl.getBoundingClientRect();
                    chartContainer.style.width = `${canvRect.width}px`;
                    chartContainer.style.height = `${canvRect.height}px`;
                    chartContainer.style.overflow = 'visible';
                }

                const textElement = chartContainer.querySelector('.position-absolute.text-center');
                if (textElement) {
                    textElement.classList.remove('top-50', 'start-50', 'translate-middle');
                    Object.assign(textElement.style, {
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        margin: '0',
                        padding: '0',
                        width: 'auto',
                        height: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        pointerEvents: 'none',
                        zIndex: '10'
                    });
                }
            });
        }
    };

    // ✅ CONFIGURACIÓN ESPECÍFICA PARA FILAS DE RECORRIDOS CON GOOGLE MAPS
    if (elementType && elementType.includes('recorridos-fila')) {
        return {
            ...baseOptions,
            scale: 2.5,
            width: element.scrollWidth,
            height: element.scrollHeight,
            // ✅ CONFIGURACIÓN ESPECIALIZADA PARA GOOGLE MAPS
            allowTaint: true,  // ← PERMITIR CONTENIDO EXTERNO
            useCORS: false,    // ← DESACTIVAR CORS PARA GOOGLE MAPS
            ignoreElements: (element) => {
                // ✅ IGNORAR ELEMENTOS PROBLEMÁTICOS DE GOOGLE MAPS
                if (element.classList && (
                    element.classList.contains('gm-control-active') ||
                    element.classList.contains('gm-fullscreen-control') ||
                    element.classList.contains('gmnoprint') ||
                    element.tagName === 'BUTTON' ||
                    element.getAttribute('jsaction')
                )) {
                    return true;
                }
                return false;
            },
            onclone: (clonedDoc) => {
                // ✅ TU CÓDIGO ORIGINAL DE BADGES Y DONAS (MANTENER)
                console.log('🔧 === REEMPLAZO COMPLETO DE BADGES ===');
                const badges = clonedDoc.querySelectorAll('.badge.bg-200');
                badges.forEach((badge, index) => {
                    const text = badge.textContent.trim();
                    const newBadge = clonedDoc.createElement('span');
                    newBadge.textContent = text;
                    newBadge.className = 'badge rounded-pill bg-custom-fix';
                    newBadge.style.cssText = `
                        background-color: #e9ecef !important;
                        color: #0d6efd !important;
                        border: 1px solid #dee2e6 !important;
                        font-size: 12px !important;
                        font-weight: 600 !important;
                        padding: 4px 8px !important;
                        border-radius: 50rem !important;
                        display: inline-block !important;
                        min-height: 20px !important;
                        line-height: 1.2 !important;
                    `;
                    badge.parentNode.replaceChild(newBadge, badge);
                });

                const chartContainers = clonedDoc.querySelectorAll('.position-relative.py-2');
                chartContainers.forEach(chartContainer => {
                    const canvasEl = chartContainer.querySelector('canvas');
                    if (canvasEl) {
                        const canvRect = canvasEl.getBoundingClientRect();
                        chartContainer.style.width = `${canvRect.width}px`;
                        chartContainer.style.height = `${canvRect.height}px`;
                        chartContainer.style.overflow = 'visible';
                    }

                    const textElement = chartContainer.querySelector('.position-absolute.text-center');
                    if (textElement) {
                        textElement.classList.remove('top-50', 'start-50', 'translate-middle');
                        Object.assign(textElement.style, {
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            margin: '0',
                            padding: '0',
                            width: 'auto',
                            height: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            pointerEvents: 'none',
                            zIndex: '10'
                        });
                    }
                });

                // ✅ PROCESAMIENTO ESPECÍFICO PARA GOOGLE MAPS
                console.log('🗺️ Procesando Google Maps para captura...');
                
                // ✅ ENCONTRAR CONTENEDORES DE GOOGLE MAPS
                const mapContainers = clonedDoc.querySelectorAll(
                    '.gm-style, [class*="google-map"], [id*="mapa-"], [id*="map-"]'
                );
                
                mapContainers.forEach((mapContainer, index) => {
                    console.log(`🗺️ Procesando mapa ${index + 1}`);
                    
                    // ✅ REMOVER CONTROLES PROBLEMÁTICOS
                    const controlsToRemove = mapContainer.querySelectorAll(
                        '.gm-control-active, .gm-fullscreen-control, .gmnoprint, ' +
                        'button, [jsaction], .gm-svpc, .gm-bundled-control'
                    );
                    controlsToRemove.forEach(control => control.remove());
                    
                    // ✅ ESTABILIZAR CANVAS DE GOOGLE MAPS
                    const canvases = mapContainer.querySelectorAll('canvas');
                    canvases.forEach((canvas, canvasIndex) => {
                        console.log(`🖼️ Canvas ${canvasIndex + 1}: ${canvas.width}x${canvas.height}`);
                        
                        // ✅ VALIDAR QUE EL CANVAS TENGA DIMENSIONES VÁLIDAS
                        if (canvas.width <= 0 || canvas.height <= 0) {
                            console.warn(`⚠️ Canvas con dimensiones inválidas, estableciendo mínimas`);
                            canvas.width = Math.max(canvas.width, 300);
                            canvas.height = Math.max(canvas.height, 200);
                        }
                        
                        // ✅ APLICAR ESTILOS PARA ASEGURAR RENDERIZADO
                        canvas.style.imageRendering = 'auto';
                        canvas.style.transformOrigin = 'top left';
                        canvas.style.transform = 'none';
                        canvas.style.position = 'relative';
                        canvas.style.zIndex = '1';
                    });
                    
                    // ✅ ASEGURAR QUE EL CONTENEDOR TENGA DIMENSIONES
                    if (mapContainer.style.height === '0px' || !mapContainer.style.height) {
                        mapContainer.style.height = '250px';
                    }
                    if (mapContainer.style.width === '0px' || !mapContainer.style.width) {
                        mapContainer.style.width = '100%';
                    }
                    
                    // ✅ FORZAR RECÁLCULO DE LAYOUT
                    mapContainer.style.overflow = 'hidden';
                    mapContainer.style.position = 'relative';
                });

                console.log(`✅ Procesamiento de Google Maps completado (${mapContainers.length} mapas)`);
            }
        };
    }

    // ✅ CONFIGURACIÓN ORIGINAL PARA ALERTAS (NO TOCAR)
    return baseOptions;
}


async captureElement(element, elementType = 'default') {
    try {
        console.log(`📸 Capturando elemento: ${elementType}`);

        if (!element) {
            console.warn(`⚠️ Elemento no válido: ${elementType}`);
            return null;
        }

        // ✅ VALIDACIÓN MÍNIMA DE DIMENSIONES
        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            console.warn(`⚠️ Elemento sin dimensiones válidas: ${rect.width}x${rect.height}`);
            return null;
        }

        // ✅ USAR EXACTAMENTE EL MISMO MÉTODO QUE FUNCIONA PARA ALERTAS
        const options = this.getOptionsForElementType(elementType, element);
        
        console.log(`⚙️ Opciones de captura para ${elementType}:`, {
            scale: options.scale,
            width: options.width,
            height: options.height
        });

        const canvas = await html2canvas(element, options);
        
        console.log(`✅ Canvas generado: ${canvas?.width || 0}x${canvas?.height || 0}`);
        return canvas;

    } catch (error) {
        console.error(`❌ Error capturando elemento ${elementType}:`, error);
        
        // ✅ FALLBACK CON CONFIGURACIÓN MÍNIMA
        try {
            return await html2canvas(element, {
                backgroundColor: '#ffffff',
                scale: 1.5,
                useCORS: true,
                allowTaint: false
            });
        } catch (fallbackError) {
            console.error(`❌ Error en fallback:`, fallbackError);
            return null;
        }
    }
}


async captureElement_respaldo(element, type) {
  try {
    console.log(`📸 === INICIANDO CAPTURA ${type} ===`);

    const options = {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false, // Mantener el logging al mínimo a menos que se depure html2canvas
      onclone: function(clonedDoc) {
        // --- Reemplazo completo de badges bg-200 (ESTA PARTE NO SE TOCA) ---
        console.log('🔧 === REEMPLAZO COMPLETO DE BADGES ===');
        const badges = clonedDoc.querySelectorAll('.badge.bg-200');
        console.log(`🏷️ Reemplazando ${badges.length} badges bg-200`);
        badges.forEach((badge, index) => {
          const text = badge.textContent.trim();
          console.log(`🔧 Reemplazando badge ${index + 1}: "${text}"`);
          const newBadge = clonedDoc.createElement('span');
          newBadge.textContent = text;
          newBadge.className = 'badge rounded-pill bg-custom-fix'; // Asegúrate de que esta clase esté definida o no sea necesaria
          newBadge.style.cssText = `
            background-color: #e9ecef !important;
            color: #0d6efd !important;
            border: 1px solid #dee2e6 !important;
            font-size: 12px !important;
            font-weight: 600 !important;
            padding: 4px 8px !important;
            border-radius: 50rem !important;
            display: inline-block !important;
            min-height: 20px !important;
            line-height: 1.2 !important;
          `;
          badge.parentNode.replaceChild(newBadge, badge);
          console.log(`✅ Badge ${index + 1} reemplazado completamente`);
        });
        console.log('✅ Todos los badges han sido reemplazados');

        // --- Centrado del texto central de la dona (NUEVO INTENTO) ---
        const chartContainers = clonedDoc.querySelectorAll('.position-relative.py-2'); // Contenedor del gráfico de dona

        chartContainers.forEach(chartContainer => {
          // Primero, asegurar que el contenedor del gráfico tenga dimensiones (como en tu código actual)
          const canvasEl = chartContainer.querySelector('canvas');
          if (canvasEl) {
            const canvRect = canvasEl.getBoundingClientRect();
            chartContainer.style.width = `${canvRect.width}px`;
            chartContainer.style.height = `${canvRect.height}px`;
            chartContainer.style.overflow = 'visible'; // Importante para que el texto no se corte
          } else {
            // Si no hay canvas, al menos asegurar que el contenedor tenga position relative
            // y un tamaño por defecto si es necesario, aunque esto es menos probable para gráficos.
            // La clase .position-relative ya debería encargarse de la posición.
          }

          // Ahora, encontrar el elemento de texto central
          // El selector original es '.position-absolute.top-50.start-50.translate-middle.text-center'
          const textElement = chartContainer.querySelector('.position-absolute.text-center'); 
          // Podrías usar el selector más largo si es más preciso:
          // const textElement = chartContainer.querySelector('.position-absolute.top-50.start-50.translate-middle.text-center');


          if (textElement) {
            console.log('🔧 Intentando centrar texto de dona:', textElement.innerText.substring(0,30).replace(/\s+/g, ' '));

            // Quitar clases de Bootstrap que podrían interferir con el cálculo de html2canvas
            textElement.classList.remove('top-50', 'start-50', 'translate-middle');

            // Aplicar estilos CSS estándar para centrar
            Object.assign(textElement.style, {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              margin: '0',      // Resetear márgenes
              padding: '0',     // Resetear paddings
              width: 'auto',    // Permitir que el contenido defina el ancho
              height: 'auto',   // Permitir que el contenido defina la altura
              display: 'flex',  // Usar flex para un mejor control del contenido interno del texto
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center', // Asegurar alineación del texto
              pointerEvents: 'none',
              zIndex: '10'      // Asegurar que esté por encima del gráfico
            });
            console.log('✅ Estilos de centrado aplicados a texto de dona.');
          }
        });
      }
    };

    const canvas = await html2canvas(element, options);

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas vacío después de captura');
    }

    console.log(`✅ Elemento capturado: ${canvas.width}x${canvas.height}`);
    return canvas;

  } catch (error) {
    console.error('❌ Error en captura:', error);
    // fallback simple
    return await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 1.5,
      useCORS: true
    });
  }
}





async captureElement_buenovertical(element, type) {
  try {
    console.log(`📸 === INICIANDO CAPTURA ${type} ===`);

    const options = {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false, // Mantener el logging al mínimo a menos que se depure html2canvas
      onclone: function(clonedDoc) {
        // --- Reemplazo completo de badges bg-200 (ESTA PARTE NO SE TOCA) ---
        console.log('🔧 === REEMPLAZO COMPLETO DE BADGES ===');
        const badges = clonedDoc.querySelectorAll('.badge.bg-200');
        console.log(`🏷️ Reemplazando ${badges.length} badges bg-200`);
        badges.forEach((badge, index) => {
          const text = badge.textContent.trim();
          console.log(`🔧 Reemplazando badge ${index + 1}: "${text}"`);
          const newBadge = clonedDoc.createElement('span');
          newBadge.textContent = text;
          newBadge.className = 'badge rounded-pill bg-custom-fix'; // Asegúrate de que esta clase esté definida o no sea necesaria
          newBadge.style.cssText = `
            background-color: #e9ecef !important;
            color: #0d6efd !important;
            border: 1px solid #dee2e6 !important;
            font-size: 12px !important;
            font-weight: 600 !important;
            padding: 4px 8px !important;
            border-radius: 50rem !important;
            display: inline-block !important;
            min-height: 20px !important;
            line-height: 1.2 !important;
          `;
          badge.parentNode.replaceChild(newBadge, badge);
          console.log(`✅ Badge ${index + 1} reemplazado completamente`);
        });
        console.log('✅ Todos los badges han sido reemplazados');

        // --- Centrado del texto central de la dona (NUEVO INTENTO) ---
        const chartContainers = clonedDoc.querySelectorAll('.position-relative.py-2'); // Contenedor del gráfico de dona

        chartContainers.forEach(chartContainer => {
          // Primero, asegurar que el contenedor del gráfico tenga dimensiones (como en tu código actual)
          const canvasEl = chartContainer.querySelector('canvas');
          if (canvasEl) {
            const canvRect = canvasEl.getBoundingClientRect();
            chartContainer.style.width = `${canvRect.width}px`;
            chartContainer.style.height = `${canvRect.height}px`;
            chartContainer.style.overflow = 'visible'; // Importante para que el texto no se corte
          } else {
            // Si no hay canvas, al menos asegurar que el contenedor tenga position relative
            // y un tamaño por defecto si es necesario, aunque esto es menos probable para gráficos.
            // La clase .position-relative ya debería encargarse de la posición.
          }

          // Ahora, encontrar el elemento de texto central
          // El selector original es '.position-absolute.top-50.start-50.translate-middle.text-center'
          const textElement = chartContainer.querySelector('.position-absolute.text-center'); 
          // Podrías usar el selector más largo si es más preciso:
          // const textElement = chartContainer.querySelector('.position-absolute.top-50.start-50.translate-middle.text-center');


          if (textElement) {
            console.log('🔧 Intentando centrar texto de dona:', textElement.innerText.substring(0,30).replace(/\s+/g, ' '));

            // Quitar clases de Bootstrap que podrían interferir con el cálculo de html2canvas
            textElement.classList.remove('top-50', 'start-50', 'translate-middle');

            // Aplicar estilos CSS estándar para centrar
            Object.assign(textElement.style, {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              margin: '0',      // Resetear márgenes
              padding: '0',     // Resetear paddings
              width: 'auto',    // Permitir que el contenido defina el ancho
              height: 'auto',   // Permitir que el contenido defina la altura
              display: 'flex',  // Usar flex para un mejor control del contenido interno del texto
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center', // Asegurar alineación del texto
              pointerEvents: 'none',
              zIndex: '10'      // Asegurar que esté por encima del gráfico
            });
            console.log('✅ Estilos de centrado aplicados a texto de dona.');
          }
        });
      }
    };

    const canvas = await html2canvas(element, options);

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas vacío después de captura');
    }

    console.log(`✅ Elemento capturado: ${canvas.width}x${canvas.height}`);
    return canvas;

  } catch (error) {
    console.error('❌ Error en captura:', error);
    // fallback simple
    return await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 1.5,
      useCORS: true
    });
  }
}


async captureElement_badgesgood(element, type) {
    try {
        console.log(`📸 === INICIANDO CAPTURA ${type} ===`);
        
        const options = {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            onclone: function(clonedDoc) {
                console.log('🔧 === REEMPLAZO COMPLETO DE BADGES ===');
                
                const badges = clonedDoc.querySelectorAll('.badge.bg-200');
                console.log(`🏷️ Reemplazando ${badges.length} badges bg-200`);
                
                badges.forEach((badge, index) => {
                    const text = badge.textContent.trim();
                    console.log(`🔧 Reemplazando badge ${index + 1}: "${text}"`);
                    
                    // ✅ CREAR NUEVO ELEMENTO BADGE
                    const newBadge = clonedDoc.createElement('span');
                    newBadge.textContent = text;
                    newBadge.className = 'badge rounded-pill bg-custom-fix';
                    
                    // ✅ APLICAR ESTILOS INLINE COMPLETOS
                    newBadge.style.cssText = `
                        background-color: #e9ecef !important;
                        color: #0d6efd !important;
                        border: 1px solid #dee2e6 !important;
                        font-size: 12px !important;
                        font-weight: 600 !important;
                        padding: 4px 8px !important;
                        border-radius: 50rem !important;
                        display: inline-block !important;
                        min-height: 20px !important;
                        line-height: 1.2 !important;
                    `;
                    
                    // ✅ REEMPLAZAR EL BADGE ORIGINAL
                    badge.parentNode.replaceChild(newBadge, badge);
                    
                    console.log(`✅ Badge ${index + 1} reemplazado completamente`);
                });
                
                console.log('✅ Todos los badges han sido reemplazados');

                const charts = clonedDoc.querySelectorAll('.position-relative.py-2');
                charts.forEach(chart => {
                const { width, height } = chart.getBoundingClientRect();
                chart.style.width = `${width}px`;
                chart.style.height = `${height}px`;
                chart.style.overflow = 'visible';
                
                // 2) Reposicionar el texto central
                const center = chart.querySelector('.position-absolute.text-center');
                if (center) {
                    // quitar translate-middle
                    center.style.transform = '';
                    // ubicar en el 50% real
                    center.style.left = `${width / 2}px`;
                    center.style.top = `${height / 2}px`;
                    // desplazar la mitad de su propio tamaño
                    center.style.marginLeft = `-${center.offsetWidth / 2}px`;
                    center.style.marginTop = `-${center.offsetHeight / 2}px`;
                    center.style.position = 'absolute';
                }
                });
            }
        };
        
        const canvas = await html2canvas(element, options);
        
        // ✅ VERIFICAR RESULTADO
        if (!canvas || canvas.width === 0 || canvas.height === 0) {
            throw new Error('Canvas vacío después de captura');
        }
        
        console.log(`✅ Elemento capturado: ${canvas.width}x${canvas.height}`);
        return canvas;
        
    } catch (error) {
        console.error('❌ Error en captura:', error);
        
        // ✅ FALLBACK SIMPLE
        return await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: 1.5,
            useCORS: true
        });
    }
}
    // /**
    //  * Debug específico para badges
    //  */
    // debugBadges(element, momento) {
    //     console.log(`\n🔍 === DEBUG BADGES ${momento} ===`);
        
    //     const badges = element.querySelectorAll('.badge');
    //     console.log(`🏷️ Total badges encontrados: ${badges.length}`);
        
    //     badges.forEach((badge, index) => {
    //         const rect = badge.getBoundingClientRect();
    //         const computedStyle = window.getComputedStyle(badge);
            
    //         console.log(`🏷️ Badge ${index + 1}:`, {
    //             texto: badge.textContent.trim(),
    //             clases: badge.className,
    //             dimensiones: `${rect.width}x${rect.height}`,
    //             posicion: `(${rect.left}, ${rect.top})`,
    //             esVisible: rect.width > 0 && rect.height > 0,
    //             estilos: {
    //                 backgroundColor: computedStyle.backgroundColor,
    //                 color: computedStyle.color,
    //                 fontSize: computedStyle.fontSize,
    //                 display: computedStyle.display,
    //                 visibility: computedStyle.visibility,
    //                 opacity: computedStyle.opacity,
    //                 position: computedStyle.position
    //             }
    //         });
            
    //         // 🔍 VERIFICAR CONTENIDO INTERNO
    //         const innerDiv = badge.querySelector('div');
    //         if (innerDiv) {
    //             const innerStyle = window.getComputedStyle(innerDiv);
    //             console.log(`   📝 Div interno:`, {
    //                 texto: innerDiv.textContent.trim(),
    //                 clases: innerDiv.className,
    //                 estilos: {
    //                     fontSize: innerStyle.fontSize,
    //                     color: innerStyle.color,
    //                     display: innerStyle.display,
    //                     visibility: innerStyle.visibility
    //                 }
    //             });
    //         }
    //     });
        
    //     console.log(`=== FIN DEBUG BADGES ${momento} ===\n`);
    // }

    // /**
    //  * Debug específico para canvas resultante
    //  */
    // debugCanvas(canvas, type) {
    //     console.log(`\n🎨 === DEBUG CANVAS ${type} ===`);
        
    //     if (!canvas) {
    //         console.error('❌ Canvas es null o undefined');
    //         return;
    //     }
        
    //     console.log(`📐 Dimensiones: ${canvas.width}x${canvas.height}`);
        
    //     // 🔍 VERIFICAR SI EL CANVAS ESTÁ VACÍO O NEGRO
    //     const ctx = canvas.getContext('2d');
    //     const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 100), Math.min(canvas.height, 100));
    //     const pixels = imageData.data;
        
    //     let hasColor = false;
    //     let pixelCount = 0;
    //     let colorPixels = 0;
        
    //     for (let i = 0; i < pixels.length; i += 4) {
    //         pixelCount++;
    //         const r = pixels[i];
    //         const g = pixels[i + 1];
    //         const b = pixels[i + 2];
    //         const a = pixels[i + 3];
            
    //         // Si no es negro (0,0,0) ni transparente
    //         if ((r !== 0 || g !== 0 || b !== 0) && a > 0) {
    //             hasColor = true;
    //             colorPixels++;
    //         }
    //     }
        
    //     console.log(`🎨 Análisis de pixels:`, {
    //         totalPixels: pixelCount,
    //         pixelsConColor: colorPixels,
    //         porcentajeColor: ((colorPixels / pixelCount) * 100).toFixed(2) + '%',
    //         tieneContenido: hasColor,
    //         probablementeVacio: !hasColor || (colorPixels / pixelCount) < 0.01
    //     });
        
    //     console.log(`=== FIN DEBUG CANVAS ===\n`);
    // }

    async captureElement_goog(element, type) {
        try {
            console.log(`📸 Capturando elemento tipo: ${type}`);
            
            // ✅ CONFIGURACIÓN BÁSICA Y SEGURA
            const options = {
                backgroundColor: '#ffffff',
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                // ✅ SOLUCIÓN SIMPLE: Solo agregar estilos CSS adicionales
                onclone: function(clonedDoc) {
                    // 🎯 CREAR HOJA DE ESTILOS TEMPORAL PARA BADGES
                    const style = clonedDoc.createElement('style');
                    style.textContent = `
                        .badge.bg-200, .bg-200 {
                            background-color: #e9ecef !important;
                            color: #0d6efd !important;
                            border: 1px solid #dee2e6 !important;
                            font-size: 11px !important;
                            font-weight: 600 !important;
                            padding: 4px 8px !important;
                            border-radius: 50rem !important;
                            display: inline-block !important;
                            min-height: 20px !important;
                        }
                        
                        .fs-10 {
                            font-size: 11px !important;
                            font-weight: 600 !important;
                        }
                    `;
                    clonedDoc.head.appendChild(style);
                }
            };
            
            const canvas = await html2canvas(element, options);
            
            // ✅ VERIFICAR RESULTADO
            if (!canvas || canvas.width === 0 || canvas.height === 0) {
                throw new Error('Canvas vacío después de captura');
            }
            
            console.log(`✅ Elemento capturado: ${canvas.width}x${canvas.height}`);
            return canvas;
            
        } catch (error) {
            console.error('❌ Error en captura:', error);
            
            // ✅ FALLBACK ULTRA SIMPLE
            return await html2canvas(element, {
                backgroundColor: '#ffffff',
                scale: 1.5,
                useCORS: true
            });
        }
    }

    async captureElement_rs(element, type) {
        const quality = this.options.captureQuality;
        
        return await html2canvas(element, {
            useCORS: true,
            allowTaint: true,
            scale: quality === 0.95 ? 1.5 : 1,
            logging: false,
            backgroundColor: '#ffffff',
            width: element.scrollWidth || element.offsetWidth,
            height: element.scrollHeight || element.offsetHeight,
            scrollX: 0,
            scrollY: 0,
            ignoreElements: (el) => {
                return el.classList && (
                    el.classList.contains('tooltip') ||
                    el.classList.contains('dropdown-menu') ||
                    el.classList.contains('modal') ||
                    el.classList.contains('popover') ||
                    // Ignorar elementos de debug
                    el.id === 'print-loading' ||
                    el.classList.contains('debug-panel')
                );
            },
            onclone: (clonedDoc) => {
                // Aplicar estilos adicionales al clon
                const clonedElement = clonedDoc.querySelector(`[data-html2canvas-ignore="false"]`) || 
                                     clonedDoc.body.querySelector('*');
                
                if (clonedElement) {
                    // Asegurar que Bootstrap grid se mantenga
                    const cols = clonedElement.querySelectorAll('.col, .col-md-6, .col-xs-12, [class*="col-"]');
                    cols.forEach(col => {
                        col.style.display = 'block';
                        col.style.float = 'left';
                        col.style.width = '50%';
                        col.style.marginBottom = '20px';
                    });
                    
                    // Ajustar filas
                    const rows = clonedElement.querySelectorAll('.row');
                    rows.forEach(row => {
                        row.style.display = 'block';
                        row.style.width = '100%';
                        row.style.marginBottom = '20px';
                        row.style.overflow = 'visible';
                    });
                }
            }
        });
    }

    /**
     * Capturar tabla independiente
     */
    async captureTable() {
        const tableSelectors = [
            '.table-container',
            '[data-table]',
            '.tanstack-table',
            'table',
            '.row:last-child .col-md-12', // Última fila que suele contener la tabla
        ];
        
        for (const selector of tableSelectors) {
            const tableContainer = document.querySelector(selector);
            
            if (tableContainer && this.isElementVisible(tableContainer)) {
                try {
                    // Verificar si es realmente una tabla
                    const hasTable = tableContainer.querySelector('table, .table, [role="table"]');
                    
                    if (hasTable) {
                        console.log('📋 Capturando tabla con selector:', selector);
                        
                        // Preparar tabla para captura
                        await this.prepareTableForCapture(tableContainer);
                        
                        const canvas = await this.captureElement(tableContainer, 'table');
                        
                        // Restaurar tabla
                        await this.restoreElementAfterCapture(tableContainer);
                        
                        return canvas;
                    }
                } catch (error) {
                    console.warn('Error capturando tabla:', error);
                }
            }
        }
        
        return null;
    }


analyzeReportStructure() {
    // ✅ SI HAY captureContainer ESPECÍFICO, USARLO
    const captureContainer = this.options?.captureContainer;
    let baseContainer = null;
    
    if (captureContainer) {
        baseContainer = document.querySelector(captureContainer);
        if (baseContainer) {
            console.log(`✅ Usando contenedor específico: ${captureContainer}`);
        } else {
            console.warn(`❌ Contenedor ${captureContainer} no encontrado`);
        }
    }
    
    // ✅ FALLBACK AUTOMÁTICO SI NO HAY CONTENEDOR ESPECÍFICO
    if (!baseContainer) {
        baseContainer = this.findReportContainer();
        console.log(`✅ Usando contenedor automático`);
    }
    
    if (!baseContainer) {
        console.error('❌ No se encontró ningún contenedor válido');
        return this.getEmptyStructure();
    }
    
    console.log(`📊 Analizando estructura en contenedor:`, baseContainer);
    
    const analysis = {
        reportType: this.detectReportTypeFromDOM(),
        layout: this.detectLayoutPattern(baseContainer),
        components: this.detectComponentTypes(baseContainer),
        tableLocation: this.detectTableLocation(baseContainer),
        hasScrollableContent: this.hasAnyScrollableContent(baseContainer),
        containerElement: baseContainer
    };
    
    console.log('🔍 Análisis de estructura:', analysis);
    return analysis;
}

/**
 * NUEVO: Validar que el contenedor tenga contenido de reporte
 */
validateReportContainer(container) {
    // ✅ VERIFICAR QUE TENGA FILAS Y COMPONENTES VISUALES
    const rows = container.querySelectorAll('.row');
    const cards = container.querySelectorAll('.card');
    const visualComponents = container.querySelectorAll('canvas, .leaflet-container, .chart-container');
    
    console.log(`📊 Validación contenedor:`, {
        rows: rows.length,
        cards: cards.length,
        visualComponents: visualComponents.length
    });
    
    // Debe tener al menos 1 fila, 2 cards y 1 componente visual
    return rows.length >= 1 && cards.length >= 2 && visualComponents.length >= 1;
}

findReportContainer() {
    // ✅ BUSCAR CONTENEDORES ESTÁNDAR PRIMERO
    const standardContainers = [
        '#reporte-content',
        '#report-content', 
        '[data-report-area]'
    ];
    
    for (const selector of standardContainers) {
        const container = document.querySelector(selector);
        if (container) {
            console.log(`✅ Contenedor estándar encontrado: ${selector}`);
            return container;
        }
    }
    
    // ✅ BUSCAR PATRÓN DE ALERTAS (.g-3 con múltiples .row.g-3.mb-3)
    const alertasContainer = document.querySelector('.g-3');
    if (alertasContainer) {
        const alertasRows = alertasContainer.querySelectorAll('.row.g-3.mb-3');
        if (alertasRows.length >= 2) {
            console.log(`✅ Contenedor de alertas encontrado con ${alertasRows.length} filas`);
            return alertasContainer;
        }
    }
    
    // ✅ BUSCAR PRIMER CONTENEDOR CON MÚLTIPLES FILAS Y COMPONENTES VISUALES
    const allContainers = document.querySelectorAll('div');
    for (const container of allContainers) {
        const rows = container.querySelectorAll('.row');
        const visualComponents = container.querySelectorAll('canvas, .leaflet-container, .chart-container');
        
        if (rows.length >= 2 && visualComponents.length >= 2) {
            if (container !== document.body && !container.classList.contains('navbar')) {
                console.log(`✅ Contenedor genérico encontrado con ${rows.length} filas y ${visualComponents.length} componentes`);
                return container;
            }
        }
    }
    
    console.warn('⚠️ No se encontró contenedor específico, usando body');
    return document.body;
}


    detectLayoutPattern() {
        const container = document.querySelector('.container, .container-fluid, main, #root > div');
        if (!container) return 'unknown';
        
        const rows = container.querySelectorAll('.row');
        const analysis = {
            totalRows: rows.length,
            rowPatterns: [],
            isGridBased: rows.length > 0
        };
        
        rows.forEach((row, index) => {
            const cols = row.querySelectorAll('[class*="col-"]');
            const hasTable = this.isTableOnlyRow(row);
            const hasCharts = this.hasVisualContent(row);
            
            analysis.rowPatterns.push({
                rowIndex: index + 1,
                columns: cols.length,
                contentType: hasTable ? 'table' : hasCharts ? 'visual' : 'mixed',
                hasFixedHeight: row.querySelector('[style*="height"]') !== null
            });
        });
        
        return analysis;
    }

detectComponentTypes(baseContainer) {
    const components = [];
    
    // ✅ BUSCAR SOLO CARDS DENTRO DEL CONTENEDOR ESPECÍFICO
    const cards = baseContainer.querySelectorAll('.card');
    
    console.log(`🔍 Cards encontradas en ${baseContainer.id || 'contenedor'}: ${cards.length}`);
    
    // ✅ FILTRAR CARDS VÁLIDAS
    const cardsValidas = Array.from(cards).filter(card => {
        // ✅ VERIFICAR QUE SEA VISIBLE
        if (!this.isElementVisible(card)) {
            console.log(`⏭️ Card omitida: no visible`);
            return false;
        }
        
        // ✅ VERIFICAR QUE TENGA CONTENIDO VISUAL
        const hasVisualContent = !!(
            card.querySelector('canvas') ||
            card.querySelector('.leaflet-container') ||
            card.querySelector('.chart-container') ||
            card.querySelector('.echarts-container') ||
            card.querySelector('table') ||
            card.querySelector('svg')
        );
        
        if (!hasVisualContent) {
            console.log(`⏭️ Card omitida: sin contenido visual`);
            return false;
        }
        
        return true;
    });
    
    console.log(`✅ Cards válidas: ${cardsValidas.length}`);
    
    cardsValidas.forEach((card, index) => {
        const cardTitle = this.getCardTitle(card);
        
        const componentInfo = {
            index: index + 1,
            element: card,
            type: this.classifyCardComponent(card, cardTitle),
            hasScroll: this.hasScrollableContent(card),
            isTable: this.isTableOnlyCard(card),
            size: this.getElementSize(card),
            row: this.getRowIndex(card, baseContainer),
            title: cardTitle
        };
        
        console.log(`📊 Componente válido ${index + 1}:`, {
            type: componentInfo.type,
            title: cardTitle,
            element: card.tagName,
            classes: card.className.substring(0, 50) + '...'
        });
        
        components.push(componentInfo);
    });
    
    console.log(`✅ Total componentes detectados: ${components.length}`);
    return components;
}

/**
 * NUEVO: Verificar si una card está vacía
 */
isCardEmpty(card) {
    const cardBody = card.querySelector('.card-body');
    if (!cardBody) return true;
    
    // Verificar si tiene contenido mínimo
    const textContent = cardBody.textContent.trim();
    const hasVisualContent = cardBody.querySelector('canvas, .leaflet-container, .chart-container, table');
    
    return textContent.length < 10 && !hasVisualContent;
}

/**
 * NUEVO: Obtener título de la card
 */
getCardTitle(card) {
    // Buscar título en header
    const cardHeader = card.querySelector('.card-header h6, .card-header h5, .card-header h4');
    if (cardHeader) return cardHeader.textContent.trim();
    
    // Buscar en FalconCardHeader
    const falconHeader = card.querySelector('[class*="falcon"] h6, [class*="falcon"] h5');
    if (falconHeader) return falconHeader.textContent.trim();
    
    return 'Componente';
}

/**
 * NUEVO: Clasificar componente por card completa
 */
classifyCardComponent(card, title) {
    const text = (card.textContent + ' ' + title).toLowerCase();
    
    // ✅ DETECCIÓN POR TÍTULO Y CONTENIDO
    if (text.includes('mapa') || text.includes('recorrido') || card.querySelector('.leaflet-container, .google-map')) {
        return 'location-map';
    }
    if (text.includes('velocidad') || text.includes('speed')) {
        return 'speed-chart';
    }
    if (text.includes('kilometraje') || text.includes('kilómetro') || text.includes('distance')) {
        return 'distance-chart';
    }
    if (text.includes('visita') || text.includes('frecuente')) {
        return 'visits-chart';
    }
    if (text.includes('actividad') || text.includes('activity')) {
        return 'activity-chart';
    }
    if (text.includes('tabla') || text.includes('listado') || card.querySelector('table')) {
        return 'table';
    }
    
    // ✅ DETECCIÓN POR ELEMENTOS VISUALES
    if (card.querySelector('canvas')) return 'chart';
    if (card.querySelector('.leaflet-container, .google-map')) return 'location-map';
    
    return 'generic-visual';
}

/**
 * NUEVO: Verificar si es card solo de tabla
 */
isTableOnlyCard(card) {
    const hasTable = card.querySelector('table, .table, [role="table"]');
    const hasOtherContent = card.querySelector('canvas, .leaflet-container, .chart-container');
    
    return hasTable && !hasOtherContent;
}



    detectComponentTypes_rs() {
        const components = [];
        const visualContainers = document.querySelectorAll(`
            [style*="height"],
            .card,
            .chart-container,
            [class*="chart"],
            [id*="chart"],
            [class*="map"],
            [id*="map"]
        `);
        
        visualContainers.forEach((container, index) => {
            if (this.isElementVisible(container)) {
                components.push({
                    index: index + 1,
                    element: container,
                    type: this.classifyComponent(container),
                    hasScroll: this.hasScrollableContent(container),
                    isTable: this.hasTableOnly(container),
                    size: this.getElementSize(container),
                    row: this.getRowIndex(container)
                });
            }
        });
        
        return components;
    }

    classifyComponent_rs(element) {
        const text = element.textContent.toLowerCase();
        
        if (element.querySelector('canvas')) return 'chart';
        if (element.querySelector('.leaflet-container, .google-map, [class*="map"]')) return 'map';
        if (element.querySelector('table, .table, [role="table"]')) return 'table';
        if (text.includes('vehículo') || text.includes('vehicle')) return 'vehicle-chart';
        if (text.includes('alerta') || text.includes('alert')) return 'alert-chart';
        if (text.includes('velocidad') || text.includes('speed')) return 'speed-chart';
        if (text.includes('kilometraje') || text.includes('distance')) return 'distance-chart';
        
        return 'generic-visual';
    }

    detectReportTypeFromDOM() {
    // Buscar en el título de la página
        const pageTitle = document.querySelector('h4, h3, h2, h1')?.textContent?.toLowerCase() || '';
        
        if (pageTitle.includes('recorrido')) return 'recorridos';
        if (pageTitle.includes('alerta')) return 'alertas';
        if (pageTitle.includes('combustible')) return 'combustible';
        if (pageTitle.includes('velocidad')) return 'velocidad';
        if (pageTitle.includes('panico')) return 'panico';
        
        // Buscar por URL
        const currentPath = window.location.pathname.toLowerCase();
        if (currentPath.includes('recorrido')) return 'recorridos';
        if (currentPath.includes('alerta')) return 'alertas';
        
        console.log(`🔍 Tipo detectado: recorridos (por URL: ${currentPath})`);
        return 'recorridos'; // Default para tu caso
    }


    /**
 * Obtener tamaño de columna Bootstrap
 */
getColumnSize(col) {
    const classes = col.className;
    if (classes.includes('col-md-12')) return 12;
    if (classes.includes('col-md-6')) return 6;
    if (classes.includes('col-md-4')) return 4;
    if (classes.includes('col-md-3')) return 3;
    return 6; // default
}

/**
 * Obtener índice de fila del elemento
 */
getRowIndex(element) {
    const row = element.closest('.row');
    if (!row) return 0;
    
    const allRows = document.querySelectorAll('.row');
    return Array.from(allRows).indexOf(row) + 1;
}

/**
 * Detectar ubicación de la tabla
 */
detectTableLocation() {
    const tables = document.querySelectorAll('table, [role="table"], .table');
    if (tables.length === 0) return null;
    
    const firstTable = tables[0];
    const row = firstTable.closest('.row');
    return {
        rowIndex: this.getRowIndex(firstTable),
        isLastRow: !row?.nextElementSibling
    };
}

/**
 * Verificar si hay contenido con scroll en toda la página
 */
hasAnyScrollableContent() {
    const scrollElements = this.findScrollableElements(document.body);
    return scrollElements.length > 0;
}

/**
 * Obtener dimensiones del elemento
 */
getElementSize(element) {
    const rect = element.getBoundingClientRect();
    return {
        width: rect.width,
        height: rect.height
    };
}


/**
 * Verificar si tiene contenido con scroll
 */
hasScrollableContent(element) {
    const scrollElements = this.findScrollableElements(element);
    return scrollElements.length > 0;
}




/***/

async prepareRecorridosTableForCapture(container) {
    console.log('🚗 Preparando RecorridosTableList para captura...');
    
    if (!container) return;

    try {
        // ✅ BUSCAR TABLA DENTRO DEL CONTENEDOR
        const tableElement = container.querySelector('table');
        
        if (!tableElement) {
            console.warn('⚠️ No se encontró tabla en RecorridosTableList');
            return;
        }

        // ✅ REMOVER SCROLL DEL CONTENEDOR DE LA TABLA
        const scrollableContainer = container.querySelector('.overflow-hidden') ||
                                   container.querySelector('[style*="overflow"]') ||
                                   tableElement.closest('.card-body');
        
        if (scrollableContainer) {
            console.log('🔧 Removiendo scroll del contenedor de tabla');
            scrollableContainer.style.overflow = 'visible';
            scrollableContainer.style.maxHeight = 'none';
            scrollableContainer.style.height = 'auto';
        }

        // ✅ ASEGURAR VISIBILIDAD DE TODAS LAS FILAS
        const allRows = tableElement.querySelectorAll('tbody tr');
        allRows.forEach((row, index) => {
            row.style.display = 'table-row';
            row.style.visibility = 'visible';
            row.style.opacity = '1';
            
            // ✅ ASEGURAR QUE LAS CELDAS SEAN VISIBLES
            const cells = row.querySelectorAll('td, th');
            cells.forEach(cell => {
                cell.style.visibility = 'visible';
                cell.style.opacity = '1';
            });
        });

        // ✅ AJUSTAR BOTONES DE UBICACIÓN PARA IMPRESIÓN
        const locationButtons = container.querySelectorAll('button[class*="btn-light"]');
        locationButtons.forEach(button => {
            if (button.querySelector('.fa-map-marker-alt')) {
                // ✅ REEMPLAZAR BOTÓN CON TEXTO
                const textNode = document.createElement('span');
                textNode.textContent = 'Ver Ubicación';
                textNode.className = 'text-primary';
                button.parentNode.replaceChild(textNode, button);
            }
        });

        // ✅ OPTIMIZAR ESTILOS DE TABLA PARA IMPRESIÓN
        tableElement.style.tableLayout = 'fixed';
        tableElement.style.width = '100%';
        
        console.log(`✅ RecorridosTableList preparada: ${allRows.length} filas visibles`);

    } catch (error) {
        console.error('❌ Error preparando RecorridosTableList:', error);
    }
}

/**
 * ✅ ACTUALIZAR: Función prepareTableForCapture principal
 */
async prepareTableForCapture(container) {
    if (!container) return;

    try {
        // ✅ DETECTAR TIPO DE TABLA
        const isRecorridosTable = container.querySelector('.card-header') && 
                                 container.querySelector('table') &&
                                 (container.textContent.includes('Recorrido') || 
                                  container.textContent.includes('Evento') ||
                                  container.textContent.includes('Marcador'));

        const isAlertasTanstack = container.querySelector('.alert-row[data-cell]');

        // ✅ PREPARAR SEGÚN TIPO
        if (isRecorridosTable) {
            console.log('🚗 Preparando RecorridosTableList...');
            await this.prepareRecorridosTableForCapture(container);
        } else if (isAlertasTanstack) {
            console.log('🚨 Preparando AlertaTableListTanstack...');
            await this.prepareAlertaTableForCapture(container);
        } else {
            console.log('📊 Preparando tabla HTML estándar...');
            await this.prepareStandardTableForCapture(container);
        }

    } catch (error) {
        console.error('❌ Error en prepareTableForCapture:', error);
    }
}

/**
 * ✅ NUEVO: Preparar tabla HTML estándar (fallback)
 */
async prepareStandardTableForCapture(container) {
    console.log('📊 Preparando tabla estándar para captura...');
    
    try {
        const tableElement = container.querySelector('table');
        
        if (tableElement) {
            // ✅ REMOVER RESTRICCIONES DE ALTURA
            tableElement.style.height = 'auto';
            tableElement.style.maxHeight = 'none';
            
            // ✅ HACER TODAS LAS FILAS VISIBLES
            const rows = tableElement.querySelectorAll('tr');
            rows.forEach(row => {
                row.style.display = 'table-row';
                row.style.visibility = 'visible';
            });
        }

        // ✅ REMOVER SCROLL DEL CONTENEDOR
        const scrollContainer = container.querySelector('.overflow-hidden, [style*="overflow"]');
        if (scrollContainer) {
            scrollContainer.style.overflow = 'visible';
            scrollContainer.style.maxHeight = 'none';
        }

    } catch (error) {
        console.error('❌ Error preparando tabla estándar:', error);
    }
}



}

export default CaptureManager;